// src/.MODULS/UCAT_EXAM_MODUL/Pages/ReviewScreen/UcatTestReview.tsx
import React, { useState, useReducer, useEffect, useRef } from "react";
import { useLocation, useNavigate, useBlocker } from "react-router-dom";
import {
    examReducer,
    initialState,
} from "../../Components/PracticeTestReducer/TestReducer";
import {
    SplitPane,
    SinglePageQuestion,
    NavigatorModal,
    ExplanationModal,
} from "../../Components/Practice_Test/Exam_component";
import { UcatLayout } from "../../Components/Shared";
import {
    ChevronLeft,
    ChevronRight,
    LogOut,
    CheckCircle,
    XCircle,
    AlertCircle,
    Flag,
} from "lucide-react";
import { listReViewExamID } from "../../../../Redux/Slices/ReViewExam/ListReViewExamSlice";
import { listReViewExamIDPractice } from "../../../../Redux/Slices/ReViewExamPractice/ListReViewExamPracticeSlice";
import { CircularProgress } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { UserAnswer, AnswerType } from "../../Components/sample data/Type";
import { addFalgExam } from "../../../../Redux/Slices/FalgExam/AddFalgExamSlice";
import { listFlag } from "../../../../Redux/Slices/Falg/ListFlagSlice";
import { listFlagExam } from "../../../../Redux/Slices/FalgExam/ListFlagExamSlice";
import { addFalg } from "../../../../Redux/Slices/Falg/AddFalgSlice";
import { listEvaluation } from "../../../../Redux/Slices/Exam/ListEvaluationSlice";
import { listEvaluationPractice } from "../../../../Redux/Slices/PracticeExam/ListEvaluationPracticeSlice";

export const UcatTestReview = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    // Get studentExamId and filterType from location state
    const navigationState = location.state as any;

    // Persist state to handle reloads
    useEffect(() => {
        if (navigationState && (typeof navigationState === 'object' || typeof navigationState === 'string' || typeof navigationState === 'number')) {
            sessionStorage.setItem("ucat_review_state", JSON.stringify(navigationState));
        }
    }, [navigationState]);

    const persistedStateStr = sessionStorage.getItem("ucat_review_state");
    const persistedState = persistedStateStr ? JSON.parse(persistedStateStr) : null;
    const activeState = (navigationState !== null && navigationState !== undefined) ? navigationState : persistedState;

    const studentExamId = activeState?.studentExamId || (typeof activeState !== 'object' ? activeState : activeState?.id);
    const filterType = activeState?.filterType || "all";

    // Improved evalAs detection:
    // 1. Explicitly passed in state
    // 2. Fallback to sessionStorage exam mode
    // 3. Default to "test" for /ucat_test_review if still ambiguous (historically safer)
    const evalAs = activeState?.evalAs ||
        (sessionStorage.getItem("ucat_exam_mode") === "exam_practice" ? "exam" : "test");

    console.log("🔍 Active state:", { studentExamId, filterType, evalAs });

    const { data: listReViewExamIDSlice, isLoading: LoadingMock } = useAppSelector(
        (state) => state.ListReViewExamIDSlice || {}
    );

    const { data: listReViewExamPracticeSlice, isLoading: LoadingPractice } = useAppSelector(
        (state: any) => state.ListReViewExamIDPracticeSlice || {}
    );

    const { data: listFlagSlice } = useAppSelector(
        (state) => state.ListFlagSlice
    );

    const { data: listFlagExamSlice } = useAppSelector(
        (state: any) => state.ListFlagExamSlice || {}
    );

    const { data: addFlagRes } = useAppSelector(
        (state) => state.AddFalgSlice
    );

    const { data: addFlagExamRes } = useAppSelector(
        (state) => state.AddFalgExamSlice
    );

    const { data: listEvaluationSlice } = useAppSelector(
        (state) => state.ListEvaluationSlice || {}
    );

    const { data: listEvaluationPracticeSlice } = useAppSelector(
        (state) => state.ListEvaluationPracticeSlice || {}
    );

    const isLoading = LoadingMock || LoadingPractice;

    const [state, dispatchReducer] = useReducer(examReducer, initialState);
    const [showNavigator, setShowNavigator] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);

    // Blocker implementation
    const blockerInitializedRef = useRef(false);
    const isSubmittingRef = useRef(false);

    const blocker = useBlocker(
        ({ currentLocation, nextLocation, historyAction }) => {
            // Prevent multiple blocker initializations
            if (!blockerInitializedRef.current) {
                blockerInitializedRef.current = true;
            }

            // 1. ALWAYS block browser back/forward buttons (POP)
            if (historyAction === "POP") {
                return true;
            }

            // 2. Allow if we are explicitly submitting/skipping
            if (isSubmittingRef.current) {
                return false;
            }

            // 3. Allow if the path isn't changing (e.g. hash changes, which we don't use but good for safety)
            if (currentLocation.pathname === nextLocation.pathname) {
                return false;
            }

            // 4. Specifically check allowed transition paths for programmatic navigation
            const allowedPaths = ["/ucat_exampractice_instruction", "/ucat_examresult_practice", "/ucat_examsummary_practice", "/ucat_test", "/ucat_test_instructions", "/practicetest", "/welcome_ucat"];
            const isAllowedTarget = allowedPaths.includes(nextLocation.pathname);

            // Block everything else
            console.warn("BLOCKER", nextLocation.pathname);
            return !isAllowedTarget;
        }
    );

    const [showReloadWarning, setShowReloadWarning] = useState(false);

    useEffect(() => {
        if (blocker.state === "blocked") {
            setShowReloadWarning(true);
        } else {
            setShowReloadWarning(false);
        }
    }, [blocker.state]);

    // Cleanup blocker when component unmounts to prevent multiple blocker conflicts
    useEffect(() => {
        return () => {
            setShowReloadWarning(false);
            if (blocker.state === "blocked") {
                blocker.reset();
            }
        };
    }, [blocker]);

    const handleReloadConfirm = () => {
        isSubmittingRef.current = true;
        if (blocker.state === "blocked") {
            blocker.reset();
        }
        setShowReloadWarning(false);
    };

    // Map server response data to frontend UserAnswer format
    const mapServerAnswersToFrontendFormat = (questions: any[]): Record<number, UserAnswer> => {
        const mappedAnswers: Record<number, UserAnswer> = {};

        questions.forEach((q: any) => {
            const qId = q.question_id || q.id;
            const chosenOption = q.options?.find((opt: any) => opt.chosen);
            const selectedOptionIndex = chosenOption ? q.options.findIndex(
                (opt: any) => opt.option_id === chosenOption.option_id
            ) : null;

            let dragDropAnswers = undefined;
            if (q.answer_type === AnswerType.DRAG_DROP) {
                const rawAnswers = q.answer_data || q.sentence;
                // Prioritize answer_items as it contains the processed structured review data
                if (q.answer_items && Array.isArray(q.answer_items) && q.answer_items.length > 0) {
                    dragDropAnswers = {};
                    q.answer_items.forEach((item: any) => {
                        const subId = item.drag_drop?.id || item.drag_drop_id || item.id;
                        if (subId) {
                            // "value" is what the user dragged into the bucket
                            dragDropAnswers[subId] = item.value || item.answer || null;
                        }
                    });
                } else if (rawAnswers && typeof rawAnswers === 'string' && rawAnswers !== "null" && rawAnswers !== "{}" && rawAnswers.trim() !== "") {
                    try {
                        dragDropAnswers = JSON.parse(rawAnswers);
                    } catch (e) {
                        console.error("Failed to parse review drag drop answer", e);
                    }
                }
            }

            if (qId) {
                const currentEvalAs = evalAs || "exam";
                const relevantFlagSlice = evalAs === "exam" ? listFlagExamSlice : listFlagSlice;
                const responseObj = relevantFlagSlice?.data as any;
                const flagItems = responseObj?.data || (Array.isArray(relevantFlagSlice?.data) ? relevantFlagSlice?.data : null);
                const hasFlagsLoaded = Array.isArray(flagItems);

                const serverFlag = hasFlagsLoaded ? flagItems.find((f: any) => Number(f.question_id) === Number(qId)) : null;

                // Strict source of truth: if the flag data has been fetched, the server list is our master.
                const isFlagged = hasFlagsLoaded
                    ? !!serverFlag?.flagged
                    : (q.flagged === true || q.isFlagged === true || q.is_flagged === true || q.is_flagged === 1 || q.is_flagged === "true");

                mappedAnswers[Number(qId)] = {
                    selectedOptionIndex,
                    dragDropAnswers,
                    isFlagged: isFlagged,
                    isViewed: true,
                };
            }
        });

        return mappedAnswers;
    };

    const reviewDataRaw = evalAs === "exam"
        ? (listReViewExamIDSlice?.data || listReViewExamIDSlice)
        : (listReViewExamPracticeSlice?.data || listReViewExamPracticeSlice);
    const reviewData: any = reviewDataRaw;
    const allQuestions = reviewData?.questions || [];

    // Debug: Log raw API response to see structure
    console.log("🔍 Raw Review API Response:", {
        evalAs,
        reviewDataRaw,
        reviewData,
        allQuestionsCount: allQuestions.length,
        firstQuestion: allQuestions[0] ? {
            question_id: allQuestions[0].question_id,
            hasPrompt: !!allQuestions[0].prompt,
            promptLength: allQuestions[0].prompt?.length || 0,
            promptPreview: allQuestions[0].prompt?.substring(0, 100) + (allQuestions[0].prompt?.length > 100 ? "..." : ""),
            hasInstruction: !!allQuestions[0].instruction,
            instructionLength: allQuestions[0].instruction?.length || 0,
            fullQuestion: allQuestions[0]
        } : null
    });

    // Memoize normalized questions so they don't change on every render
    const normalizedQuestions = React.useMemo(() => {
        const normalized = allQuestions.map((q: any) => {
            let finalOptions = (q.options || []).map((opt: any) => {
                // Determine chosen status
                const isChosenByText = q.answer !== undefined && q.answer !== null &&
                    String(opt.option_text || opt.option || "").toLowerCase() === String(q.answer).toLowerCase();
                const isChosenByBool = q.answer !== undefined && q.answer !== null && (
                    (String(q.answer).toLowerCase() === "true" && (String(opt.option_text).toLowerCase() === "yes" || String(opt.option_text).toLowerCase() === "true")) ||
                    (String(q.answer).toLowerCase() === "false" && (String(opt.option_text).toLowerCase() === "no" || String(opt.option_text).toLowerCase() === "false"))
                );

                const optId = opt.option_id ?? opt.id ?? null;
                const isChosenById = q.chosen_option_id != null && optId != null && Number(q.chosen_option_id) === Number(optId);

                // Map is_correct property from any common server-side naming
                const isCorrect = opt.is_correct === true || opt.is_correct === 1 || opt.answer === true || opt.answer === "true" || opt.answer === 1;

                return {
                    ...opt,
                    option_id: optId,
                    option_text: opt.option_text ?? opt.option ?? opt.answer ?? null,
                    option_url: opt.option_url ?? opt.image_url ?? null,
                    is_correct: isCorrect,
                    chosen: !!(opt.chosen || isChosenById || isChosenByText || isChosenByBool),
                };
            });

            if (q.answer_type === AnswerType.TRUE_FALSE && (!finalOptions || finalOptions.length === 0)) {
                const qAnswerLower = String(q.answer || "").toLowerCase();
                finalOptions = [
                    {
                        option_id: -1,
                        option_text: "Yes",
                        option_url: null,
                        chosen: q.chosen_option_id === -1 || qAnswerLower === "true" || qAnswerLower === "yes"
                    },
                    {
                        option_id: -2,
                        option_text: "No",
                        option_url: null,
                        chosen: q.chosen_option_id === -2 || qAnswerLower === "false" || qAnswerLower === "no"
                    },
                ];
            }

            return {
                ...q,
                question_id: q.question_id || q.id,
                question_text: q.question_text || q.question || null,
                question_url: q.question_url || q.image_url || null,
                prompt: q.prompt || null,
                prompt_url: q.prompt_url || null,
                imgWidth: q.imgWidth || null,
                prompt_imgWidth: q.prompt_imgWidth || null,
                instruction: q.instruction || null,
                options: finalOptions,
                answer_type: (() => {
                    const type = String(q.answer_type || "").toUpperCase();
                    if (type === "DRAG_DROP" || type === "DRAG-DROP") return AnswerType.DRAG_DROP;
                    if (type === "TRUE_FALSE" || type === "TRUE-FALSE" || type === "YES_NO") return AnswerType.TRUE_FALSE;
                    return AnswerType.MCQ;
                })(),
                drag_drop_data: q.drag_drop_data || (q.answer_items ? q.answer_items.map((ai: any) => ai.drag_drop) : null) || null,
                drag_drop_pattern: q.drag_drop_pattern || null,
                subtype_name: q.subtype_name || null,

                correctAnswerIndex: finalOptions.findIndex((opt: any) => opt.is_correct),
                explanation: q.explanation || q.answer_explanation || q.answer_explanation_text,
                isFlagged: (() => {
                    const currentEvalAs = evalAs || "exam";
                    const relevantFlagSlice = evalAs === "exam" ? listFlagExamSlice : listFlagSlice;
                    const responseObj = relevantFlagSlice?.data as any;
                    const flagItems = responseObj?.data || (Array.isArray(relevantFlagSlice?.data) ? relevantFlagSlice?.data : null);
                    const hasFlagsLoaded = Array.isArray(flagItems);
                    const serverFlag = hasFlagsLoaded ? flagItems.find((f: any) => Number(f.question_id) === Number(q.question_id || q.id)) : null;

                    return hasFlagsLoaded
                        ? !!serverFlag?.flagged
                        : (q.flagged === true || q.isFlagged === true || q.is_flagged === true || q.is_flagged === 1 || q.is_flagged === "true");
                })(),
            };
        });

        // Debug: Log normalized questions to check if prompt field is preserved
        console.log("🔍 Normalized Questions Debug:", {
            originalCount: allQuestions.length,
            normalizedCount: normalized.length,
            firstNormalized: normalized[0] ? {
                question_id: normalized[0].question_id,
                hasPrompt: !!normalized[0].prompt,
                promptLength: normalized[0].prompt?.length || 0,
                promptPreview: normalized[0].prompt?.substring(0, 100) + (normalized[0].prompt?.length > 100 ? "..." : ""),
                hasInstruction: !!normalized[0].instruction,
                instructionLength: normalized[0].instruction?.length || 0,
                originalHasPrompt: !!allQuestions[0]?.prompt,
                originalPromptLength: allQuestions[0]?.prompt?.length || 0
            } : null
        });

        return normalized;
    }, [allQuestions, listFlagSlice, listFlagExamSlice, evalAs]);

    // Memoize filtered questions
    const filteredQuestions = React.useMemo(() => {
        let qs = normalizedQuestions;
        if (filterType === "incorrect") {
            qs = normalizedQuestions.filter((q: any) => {
                const chosenOption = q.options.find((opt: any) => opt.chosen);
                return chosenOption && !chosenOption.is_correct;
            });
        } else if (filterType === "flagged") {
            qs = normalizedQuestions.filter((q: any) => {
                return q.isFlagged === true;
            });
        }
        return qs;
    }, [normalizedQuestions, filterType]);

    // Track initialization state to prevent resetting filters/index on every render
    const isInitialized = React.useRef(false);

    // Initialize state with mapped answers when filtered questions are available
    useEffect(() => {
        if (filteredQuestions.length > 0 && !isInitialized.current) {
            const mappedAnswers = mapServerAnswersToFrontendFormat(filteredQuestions);
            dispatchReducer({
                type: "LOAD_EXISTING_ANSWERS",
                answers: mappedAnswers
            });

            // If we came from a specific question click, jump to it
            const initialIdx = activeState?.initialQuestionIndex;
            if (typeof initialIdx === 'number' && initialIdx >= 0 && initialIdx < filteredQuestions.length) {
                dispatchReducer({
                    type: "START_SECTION",
                    time: 0,
                    index: initialIdx
                });
            }

            // console.log("🔄 Initialized review session with answers.");
            isInitialized.current = true;
        }
    }, [filteredQuestions, navigationState?.initialQuestionIndex]);

    useEffect(() => {
        if (studentExamId) {
            console.log("🔍 Loading review data for:", evalAs, "exam ID:", studentExamId);
            if (evalAs === "exam") {
                dispatch(listReViewExamID({ id: studentExamId }));
                dispatch(listEvaluation({ student_exam_id: studentExamId }));
            } else {
                dispatch(listReViewExamIDPractice({ id: studentExamId }));
                dispatch(listEvaluationPractice({ student_test_id: studentExamId }));
            }
        }
    }, [dispatch, studentExamId, evalAs]);

    // Fetch flags on mount
    useEffect(() => {
        if (studentExamId) {
            if (evalAs === "exam") {
                dispatch(listFlagExam({ student_exam_id: Number(studentExamId) }));
            } else {
                dispatch(listFlag({ student_test_id: Number(studentExamId) }));
            }
        }
    }, [dispatch, studentExamId, evalAs]);

    // Re-fetch flags after toggling
    useEffect(() => {
        if (studentExamId) {
            if (evalAs === "exam") {
                dispatch(listFlagExam({ student_exam_id: Number(studentExamId) }));
            } else {
                dispatch(listFlag({ student_test_id: Number(studentExamId) }));
            }
        }
    }, [dispatch, studentExamId, addFlagRes, addFlagExamRes, evalAs]);

    // Update reducer state when flags change
    useEffect(() => {
        const relevantFlagSlice = evalAs === "exam" ? listFlagExamSlice : listFlagSlice;
        const responseObj = relevantFlagSlice?.data as any;
        const flagItems = responseObj?.data || (Array.isArray(relevantFlagSlice?.data) ? relevantFlagSlice?.data : null);
        const hasFlagsLoaded = Array.isArray(flagItems);

        if (hasFlagsLoaded && isInitialized.current) {
            // Create a set of flagged question IDs from the server for easy lookup
            const serverFlaggedIds = new Set(flagItems.filter((f: any) => !!f.flagged).map((f: any) => Number(f.question_id || f.id)));

            // Sync every question's flag status based ONLY on the server data
            filteredQuestions.forEach((q: any) => {
                const qId = Number(q.question_id || q.id);
                const isCurrentlyFlaggedInServer = serverFlaggedIds.has(qId);
                const currentFlagInState = !!state.answers[qId]?.isFlagged;

                if (currentFlagInState !== isCurrentlyFlaggedInServer) {
                    dispatchReducer({
                        type: "SYNC_FLAG",
                        questionId: qId,
                        isFlagged: isCurrentlyFlaggedInServer
                    });
                }
            });
        }
    }, [listFlagSlice, listFlagExamSlice, filteredQuestions, isInitialized.current, evalAs]);

    // Security measures to block inspection tools
    useEffect(() => {
        const handleContextMenu = (e: MouseEvent) => {
            e.preventDefault();
        };

        const handleKeyDownSecurity = (e: KeyboardEvent) => {
            // Block F12
            if (e.key === "F12") {
                e.preventDefault();
                return;
            }

            // Block Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C (Cmd+Option+I/J/C on Mac)
            if (
                (e.ctrlKey || e.metaKey) &&
                e.shiftKey &&
                (e.key === "I" || e.key === "i" || e.key === "J" || e.key === "j" || e.key === "C" || e.key === "c")
            ) {
                e.preventDefault();
                return;
            }

            // Block Ctrl+U (View Source)
            if ((e.ctrlKey || e.metaKey) && (e.key === "U" || e.key === "u")) {
                e.preventDefault();
                return;
            }
        };

        window.addEventListener("contextmenu", handleContextMenu);
        window.addEventListener("keydown", handleKeyDownSecurity, true);

        return () => {
            window.removeEventListener("contextmenu", handleContextMenu);
            window.removeEventListener("keydown", handleKeyDownSecurity, true);
        };
    }, []);


    const findQuestionsLine = (obj: any): any[] => {
        if (!obj) return [];
        if (Array.isArray(obj)) {
            if (obj[0]?.questions_line) return obj[0].questions_line;
            if (obj[0]?.question_time_taken) return obj[0].question_time_taken;
            if (obj.length > 0 && obj[0]?.question_id) return obj;
            return [];
        }
        if (obj.questions_line) return obj.questions_line;
        if (obj.question_time_taken) return obj.question_time_taken;
        if (obj.data?.questions_line) return obj.data.questions_line;
        if (obj.data?.question_time_taken) return obj.data.question_time_taken;
        if (obj.data) {
            if (Array.isArray(obj.data)) return obj.data[0]?.questions_line || obj.data[0]?.question_time_taken || [];
            if (obj.data.questions_line) return obj.data.questions_line;
            if (obj.data.question_time_taken) return obj.data.question_time_taken;
            return findQuestionsLine(obj.data);
        }
        return [];
    };

    const evaluationLines = evalAs === "exam" ? findQuestionsLine(listEvaluationSlice) : findQuestionsLine(listEvaluationPracticeSlice);
    const evaluationMap = React.useMemo(() => {
        const map = new Map();
        evaluationLines.forEach((item: any) => {
            map.set(Number(item.question_id), item);
        });
        return map;
    }, [evaluationLines]);

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center bg-white">
                <CircularProgress />
            </div>
        );
    }

    if (allQuestions.length === 0) {
        return (
            <div className="flex h-screen flex-col items-center justify-center bg-white">
                <p className="text-gray-600 mb-4">No review data available.</p>
                <button onClick={() => navigate("/welcome_ucat")} className="px-4 py-2 bg-blue-600 text-white rounded">Return to Home</button>
            </div>
        );
    }

    if (filteredQuestions.length === 0) {
        return (
            <div className="flex h-screen flex-col items-center justify-center bg-white">
                <p className="text-gray-600 mb-4">
                    {filterType === "incorrect"
                        ? "No incorrect questions found. Great job!"
                        : filterType === "flagged"
                            ? "No flagged questions found."
                            : "No questions available."}
                </p>
                <button onClick={() => navigate(-1)} className="px-4 py-2 bg-blue-600 text-white rounded">Return to Review</button>
            </div>
        );
    }

    const section = {
        id: 0,
        title: reviewData?.exam_name || "UCAT Exam Review",
        questions: filteredQuestions,
    };

    const question = section.questions[state.currentQuestionIndex];
    if (!question) {
        return (
            <div className="flex h-screen items-center justify-center bg-white">
                <CircularProgress />
            </div>
        );
    }

    // Debug: Log question data to check if prompt field is present
    console.log("🔍 Review Question Data:", {
        question_id: question.question_id,
        hasPrompt: !!question.prompt,
        promptLength: question.prompt?.length || 0,
        promptPreview: question.prompt?.substring(0, 100) + (question.prompt?.length > 100 ? "..." : ""),
        hasInstruction: !!question.instruction,
        instructionLength: question.instruction?.length || 0,
        fullQuestion: question
    });



    // Determine question status
    const userAnswer = state.answers[question.question_id];
    const isDragDrop = question.answer_type === AnswerType.DRAG_DROP;

    // Determine question status
    let isAnswered = false;
    let isCorrect = false;

    if (isDragDrop) {
        // Fallback: Check if we have answers in the state OR in the raw question data
        const ddAnswers = userAnswer?.dragDropAnswers || {};

        // If state answers are empty, try to derive from raw answer_items 
        // (This handles cases where the state might not have initialized yet)
        const effectiveDdAnswers = (Object.keys(ddAnswers).length > 0)
            ? ddAnswers
            : (() => {
                const derived: any = {};
                if (question.answer_items && Array.isArray(question.answer_items)) {
                    question.answer_items.forEach((item: any) => {
                        const subId = item.drag_drop?.id || item.drag_drop_id || item.id;
                        if (subId) derived[subId] = item.value || item.answer;
                    });
                }
                return derived;
            })();

        const items = question.drag_drop_data || [];
        const assignedCount = Object.values(effectiveDdAnswers).filter(v => v !== null && v !== "").length;
        isAnswered = assignedCount > 0;

        if (items.length > 0 && assignedCount === items.length) {
            // Check if all items are correct (case-insensitive)
            isCorrect = items.every((item: any) => {
                const userVal = String(effectiveDdAnswers[item.id] || "").toLowerCase().trim();
                const targetVal = String(item.target_bucket || "").toLowerCase().trim();
                return userVal === targetVal && userVal !== "";
            });
        }
    } else {
        const chosenOption = question.options.find((opt: any) => opt.chosen);
        isAnswered = !!chosenOption;
        isCorrect = chosenOption?.is_correct || false;
    }

    // Enhance status with Evaluation API data (Server Source of Truth)
    const evalItem = evaluationMap.get(Number(question.question_id));
    let isIncompleteTime = false;

    if (evalItem) {
        // Check for server-side confirmed answer existence
        const hasServerAnswer = (evalItem.option_id != null) ||
            (evalItem.answer_data && evalItem.answer_data !== "null" && evalItem.answer_data !== "{}") ||
            (evalItem.sentence && evalItem.sentence !== "null" && evalItem.sentence !== "{}") ||
            (evalItem.correct !== null && evalItem.correct !== "null" && evalItem.correct !== undefined);

        // Update isAnswered if server has confirmation
        isAnswered = isAnswered || hasServerAnswer;

        // If server has explicit correct status, use it
        if (hasServerAnswer) {
            isCorrect = evalItem.correct === true || evalItem.correct === "true" || evalItem.correct === 1 || evalItem.correct === "correct";
        }

        // Check time taken for incomplete status
        const timeTakenVal = evalItem.time_taken || evalItem.time_taken_seconds;
        isIncompleteTime = timeTakenVal && Number(timeTakenVal) > 0;
    }

    let statusText = "Unseen";
    let statusColor = "text-gray-600";
    let statusBg = "bg-gray-100";
    let StatusIcon = AlertCircle;

    // Logic aligned with TestResultScreen.tsx
    if (evalItem) {
        // 1. Server-side source of truth from Evaluation API
        const hasServerAnswer = (evalItem.option_id != null) ||
            (evalItem.answer_data && evalItem.answer_data !== "null" && evalItem.answer_data !== "{}") ||
            (evalItem.sentence && evalItem.sentence !== "null" && evalItem.sentence !== "{}") ||
            (evalItem.correct !== null && evalItem.correct !== "null" && evalItem.correct !== undefined);

        const isSkipped = evalItem.skipped === true || evalItem.skipped === "true" || evalItem.skipped === 1; // Not used directly in logic but good for parity
        const timeTakenVal = evalItem.time_taken || evalItem.time_taken_seconds;
        const isIncompleteTime = timeTakenVal && Number(timeTakenVal) > 0;

        if (hasServerAnswer) {
            isCorrect = evalItem.correct === true || evalItem.correct === "true" || evalItem.correct === 1 || evalItem.correct === "correct";
            if (isCorrect) {
                statusText = "Correct";
                statusColor = "text-green-600";
                statusBg = "bg-green-100";
                StatusIcon = CheckCircle;
            } else {
                statusText = "Incorrect";
                statusColor = "text-red-600";
                statusBg = "bg-red-100";
                StatusIcon = XCircle;
            }
        } else if (isIncompleteTime) {
            statusText = "Incomplete";
            statusColor = "text-red-600";
            statusBg = "bg-red-100";
            StatusIcon = AlertCircle;
        } else {
            statusText = "Unseen";
            statusColor = "text-gray-600";
            statusBg = "bg-gray-100";
            StatusIcon = AlertCircle;
        }
    } else {
        // 2. Fallback to local/review data only if no evaluation data
        if (isAnswered) {
            if (isCorrect) {
                statusText = "Correct";
                statusColor = "text-green-600";
                statusBg = "bg-green-100";
                StatusIcon = CheckCircle;
            } else {
                statusText = "Incorrect";
                statusColor = "text-red-600";
                statusBg = "bg-red-100";
                StatusIcon = XCircle;
            }
        } else if (isIncompleteTime) {
            statusText = "Incomplete";
            statusColor = "text-red-600";
            statusBg = "bg-red-100";
            StatusIcon = AlertCircle;
        }
    }


    // Determine layout (split-pane or single-page)
    const hasPassage =
        !!question.prompt || !!question.prompt_url || !!question.instruction;

    const isReadingOrVerbal = ["Reading Comprehension", "Verbal Reasoning", "Decision Making", "Quantitative Reasoning"].some(type =>
        question.question_type_name.toLowerCase().includes(type.toLowerCase())
    );



    // Set user answer to show selected option (read-only)
    // Set user answer to show selected option (read-only)
    // FIX: Prioritize data from relevant review slice (Evaluation API) as the source of truth for user choice
    let selectedOptionIndex: number | null = null;

    if (evalItem) {
        // If evaluation data exists, it IS the source of truth.
        // If option_id is present, find the matching option index.
        if (evalItem.option_id !== undefined && evalItem.option_id !== null) {
            selectedOptionIndex = question.options.findIndex((opt: any) =>
                Number(opt.option_id) === Number(evalItem.option_id)
            );
        }
        // If evalItem exists but has no option_id, it means the user SKIPPED the question -> selectedOptionIndex remains null.
        // We do NOT fallback to question.options in this case to avoid showing incorrect defaults.
    } else {
        // Fallback: Only use legacy 'chosen' property if we have NO evaluation data at all.
        const currentChosenOption = !isDragDrop ? question.options.find((opt: any) => opt.chosen) : null;
        selectedOptionIndex = currentChosenOption
            ? question.options.findIndex(
                (opt: any) => opt.option_id === currentChosenOption.option_id
            )
            : null;
    }

    const onPrevious = () => {
        if (state.currentQuestionIndex > 0) {
            const currentQuestion = section.questions[state.currentQuestionIndex];
            dispatchReducer({
                type: "VIEW_QUESTION",
                questionId: currentQuestion.question_id
            });
            dispatchReducer({ type: "PREV_QUESTION" });
        }
    };

    const onNext = () => {
        if (state.currentQuestionIndex < section.questions.length - 1) {
            const currentQuestion = section.questions[state.currentQuestionIndex];
            dispatchReducer({
                type: "VIEW_QUESTION",
                questionId: currentQuestion.question_id
            });
            dispatchReducer({ type: "NEXT_QUESTION" });
        }
    };

    const handleEndReview = () => {
        isSubmittingRef.current = true;
        navigate("/ucat_examresult_practice", {
            state: {
                ...activeState,
                studentExamId: studentExamId
            }
        });
    };

    const handleToggleFlag = () => {
        if (studentExamId && question.question_id) {
            if (evalAs === "exam") {
                dispatch(
                    addFalgExam({
                        student_exam_id: studentExamId,
                        question_id: question.question_id,
                    })
                );
            } else {
                dispatch(
                    addFalg({
                        student_test_id: studentExamId,
                        question_id: question.question_id,
                    })
                );
            }
        }

        dispatchReducer({
            type: "TOGGLE_FLAG",
            questionId: question.question_id,
        });
    };

    // Debug: Log what's being passed as passage
    const passageData = question.prompt || question.instruction || undefined;
    console.log("🔍 Passage Debug:", {
        questionId: question.question_id,
        prompt: question.prompt,
        instruction: question.instruction,
        passageValue: passageData,
        passageType: typeof passageData,
        passageLength: passageData?.length || 0
    });

    const shouldUseSplitLayout =
        section.title === "Verbal Reasoning" ||
        section.title === "Situational Judgement" ||
        section.title === "Reading Comprehension" ||
        (section.title === "Decision Making" && ((question.prompt && question.prompt.trim() !== "") || (question.prompt_url && question.prompt_url.trim() !== ""))) ||
        (section.title === "Quantitative Reasoning" && (question.prompt_url && question.prompt_url.trim() !== ""));

    return (
        <UcatLayout
            headerTitle={section.title}
            headerSubtitle={`Question ${state.currentQuestionIndex + 1} of ${section.questions.length}`}
            showToolbar={true}
            showCalculatorButton={true}
            showExplainAnswerButton={true}
            onExplainAnswerClick={() => setShowExplanation(true)}
            toolbarRightActions={
                <div className="flex items-center h-full gap-4">
                    <div
                        className={`flex items-center gap-1.5 px-3 py-1 ${statusBg} ${statusColor} rounded-md font-medium text-[13px]`}
                    >
                        <StatusIcon size={16} />
                        <span>{statusText}</span>
                    </div>

                    <button
                        onClick={handleToggleFlag}
                        className="flex items-center gap-1 text-white hover:text-[#FFFF00] px-2 h-full transition-colors"
                    >
                        <Flag
                            size={18}
                            className={
                                userAnswer?.isFlagged ? "fill-orange-400 text-orange-400" : "text-white/90"
                            }
                        />
                        <span className="text-[16px] font-normal">
                            <span className="underline decoration-1 underline-offset-2" style={{ fontSize: "17px" }}>F</span>{userAnswer?.isFlagged ? "lagged" : "lag"} for Review
                        </span>
                    </button>
                </div>
            }
            footerProps={{
                onEndExam: handleEndReview,
                onNext: state.currentQuestionIndex < section.questions.length - 1 ? onNext : handleEndReview,
                onPrevious: state.currentQuestionIndex > 0 ? onPrevious : undefined,
                onNavigator: () => setShowNavigator(true),
                showNavigator: false,
                showPrevious: state.currentQuestionIndex > 0,
                nextDisabled: false,
                previousDisabled: state.currentQuestionIndex === 0,
                leftButtonText: "End Review",
                rightButtonText: state.currentQuestionIndex === section.questions.length - 1 ? "Finish" : "Next"
            }}
        >
            {/* Main Content */}
            <div className="flex-1 overflow-hidden flex flex-col">
                <div className="p-1 flex items-center gap-2 text-[#000]-700 w-full">
                    <div className="flex-1 text-center text-[19px] font-[400] pr-10 mb-[10px] f-tahoma">
                        {reviewData?.exam_name?.split(' ')[0] || ""} Review: use this screen to review your responses and explanations
                    </div>
                </div>
                <div className="flex-1 overflow-hidden">
                    {shouldUseSplitLayout ? (
                        <SplitPane
                            passage={question.prompt || question.instruction || undefined}
                            promptUrl={question.prompt_url || undefined}
                            question={question}
                            selectedOptionIndex={selectedOptionIndex}
                            dragDropAnswers={(() => {
                                const dd = userAnswer?.dragDropAnswers || {};
                                if (Object.keys(dd).length > 0) return dd;
                                // Fallback for dragDropAnswers if state is not ready
                                const derived: any = {};
                                if (question.answer_items && Array.isArray(question.answer_items)) {
                                    question.answer_items.forEach((item: any) => {
                                        const subId = item.drag_drop?.id || item.drag_drop_id || item.id;
                                        if (subId) derived[subId] = item.value || item.answer;
                                    });
                                }
                                return derived;
                            })()}
                            onOptionSelect={() => { }} // Read-only, no action
                            isReviewMode={true}
                            onScrollStatusChange={() => { }}
                        />
                    ) : (
                        <SinglePageQuestion
                            question={question}
                            selectedOptionIndex={selectedOptionIndex}
                            passage={question.prompt || question.instruction || undefined}
                            promptUrl={question.prompt_url || undefined}
                            dragDropAnswers={(() => {
                                const dd = userAnswer?.dragDropAnswers || {};
                                if (Object.keys(dd).length > 0) return dd;
                                // Fallback for dragDropAnswers if state is not ready
                                const derived: any = {};
                                if (question.answer_items && Array.isArray(question.answer_items)) {
                                    question.answer_items.forEach((item: any) => {
                                        const subId = item.drag_drop?.id || item.drag_drop_id || item.id;
                                        if (subId) derived[subId] = item.value || item.answer;
                                    });
                                }
                                return derived;
                            })()}
                            onOptionSelect={() => { }} // Read-only, no action
                            isReviewMode={true}
                        />
                    )}
                </div>

                {/* Navigator Modal */}
                {showNavigator && (
                    <NavigatorModal
                        sections={[section]}
                        currentSectionIndex={0}
                        currentQuestionIndex={state.currentQuestionIndex}
                        onClose={() => setShowNavigator(false)}
                        onJump={(sectionIdx: number, questionIdx: number) => {
                            // Track view before jumping
                            const currentQuestion = section.questions[state.currentQuestionIndex];
                            dispatchReducer({
                                type: "VIEW_QUESTION",
                                questionId: currentQuestion.question_id
                            });
                            // Then jump to the target question
                            dispatchReducer({
                                type: "JUMP_QUESTION",
                                index: questionIdx,
                            });
                            setShowNavigator(false);
                        }}
                        answers={state.answers}
                        isOpen={showNavigator}
                    />
                )}

                <ExplanationModal
                    isOpen={showExplanation}
                    onClose={() => setShowExplanation(false)}
                    correctAnswerIndex={question.correctAnswerIndex}
                    explanation={question.explanation}
                />

                {/* Reload Warning Dialog */}
                {showReloadWarning && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                            <h3 className="text-lg font-semibold mb-4">Navigation Blocked</h3>
                            <p className="text-gray-600 mb-6">
                                Are you sure you want to leave this review session? Your progress will be saved.
                            </p>
                            <div className="flex gap-4 justify-end">
                                <button
                                    onClick={() => {
                                        blocker.reset?.();
                                        setShowReloadWarning(false);
                                    }}
                                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleReloadConfirm}
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </UcatLayout>
    );
};

export default UcatTestReview;
